<template>
  <div 
    v-if="app.allowChat || app.isTeacher()"
    class="chat-body"
  >
    <div
      v-chat-scroll="app.isRecorder ? '' : { always: false, smooth: true }"
      class="chat-messages"
      ref="chatRef"
      @scroll="trackScroll"
    >
      <ChatMessage
        v-for="message in messages"
        :key="message.from + message.to + message.time"
        :message="message"
        @room-selected="emit('roomSelected', $event)"
      />
    </div>
    <button
      v-if="app.connection.currentRoom && currentRoom?.name !== app.connection.currentRoom.name"
      class="return-to-main-chat-button"
      @click="emit('roomSelected', app.connection.currentRoom!.name)"
    >
      Return to {{ app.connection.currentRoom!.name }} Chat
    </button>
    <div class="absolute bottom-1 w-full left-0 flex justify-center" v-if="!isAtBottom">
      <button
        class="rounded-full size-8 bg-blue-100 flex items-center justify-center hover:bg-blue-200 shadow"
        @click="scrollDown"
      >
        <i class="bx bx-down-arrow-alt text-3xl text-black"></i>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { getApp } from '../models/App';
import { User } from '../models/User';
import ChatMessage from './ChatMessage.vue';
import { Room } from '../models/Room';
import { computed, useTemplateRef, ref, onMounted } from 'vue';
import { assert } from '../assert';

const emit = defineEmits<{
  (e: 'roomSelected', roomName: string): void;
}>();

const app = getApp();

const chatRef = useTemplateRef('chatRef');

const scrollDown = () => {
  if (chatRef.value) {
    chatRef.value.scrollTo({ top: chatRef.value.scrollHeight, behavior: 'smooth' });
  }
};

const isAtBottom = ref(false);
const trackScroll = () => {
  assert(chatRef.value);
  isAtBottom.value =
    !chatRef.value.scrollTop || chatRef.value.scrollHeight - chatRef.value.scrollTop <= chatRef.value.clientHeight + 1;
};

const currentRoom = computed(() => Room.get(app.currentChat) || User.get(app.currentChat));

const usersWithMessage = computed(() => {
  return User.all().filter((u) => !!u.messages.length || app.currentPrivateChat === u.id);
});

const messages = computed(() => {
  if (currentRoom.value === Room.get(app.currentChat)) {
    // Merge room messages with private messages
    return currentRoom.value?.messages.concat(...usersWithMessage.value.map((u) => u.messages)).sort((a, b) => a.time - b.time);
  }

  return currentRoom.value?.messages.filter((msg) => {
    return msg.to === app.getUser()?.id || msg.from === app.getUser()?.id;
  });
});

onMounted(trackScroll);
</script>

<style scoped>
.chat-body {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  height: 0;
  position: relative;
}

.chat-body > .chat-messages {
  display: flex;
  flex-direction: column;
  overflow: auto;
  padding: 5px;
}

.return-to-main-chat-button {
  border: none;
  line-height: 21px;
  padding: 3px 6px;
  background: var(--color-accent-icon);
  margin-left: 3px;
  color: black;
  font-weight: bold;
  border-radius: 3px;
  margin: 5px;
}
</style>
